import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import Navbar from "@components/Navbar";
import Footer from "@components/Footer";
import PropTypes from "prop-types";
import Breadcrumbs from "../components/Breadcrumbs";
import ClarityAnalytics from "../components/ClarityAnalytics";
import FollowCursor from "@components/FollowCursor";

import "lenis/dist/lenis.css";
import { FaArrowUp, FaTimes, FaWhatsapp } from "react-icons/fa";

const WHATSAPP_NUMBER = "61415240458"; // e.g. "61412345678"
const PROFILE_PIC = "/images/logo-transparentbg-colour-notext.webp"; // update with your image path
const PROFILE_NAME = "Steer Smart Support";

function MainLayout({ heroShowText, heroHeading, heroTextContent }) {
	const containerRef = useRef(null);
	// const [isHeroSectionLoaded, setIsHeroSectionLoaded] = useState(false);
	const [showBackToTop, setShowBackToTop] = useState(false);
	const [showChat, setShowChat] = useState(false);
	const [message, setMessage] = useState("");
	const lenisRef = useRef(null);
	const location = useLocation();
	const isMobile = window.innerWidth < 768;

	useEffect(() => {
		const lenis = new Lenis({
			damping: 0.65,
			duration: 1.4,
			easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
			smoothWheel: true,
			smoothTouch: false,
		});
		lenisRef.current = lenis;

		function raf(time) {
			lenis.raf(time);
			requestAnimationFrame(raf);
		}

		requestAnimationFrame(raf);

		const handleScroll = () => {
			if (window.scrollY > 300) {
				setShowBackToTop(true);
			} else {
				setShowBackToTop(false);
			}
		};

		window.addEventListener("scroll", handleScroll);

		return () => {
			lenis.destroy();
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	// Scroll to top on route change
	useEffect(() => {
		if (lenisRef.current) {
			lenisRef.current.scrollTo(0, { immediate: true });
		} else {
			window.scrollTo(0, 0);
		}
	}, [location.pathname]);

	const scrollToTop = () => {
		if (lenisRef.current) {
			lenisRef.current.scrollTo(0, { duration: 1.2 });
		} else {
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	};

	const handleSend = () => {
		if (message.trim()) {
			window.open(
				`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
				"_blank"
			);
			setMessage("");
			setShowChat(false);
		}
	};

	return (
		<div className="flex flex-col min-h-screen bg-steerBg relative overflow-x-hidden">
			{!isMobile && <FollowCursor showSmoke={false} />}
			{/* <FollowCursor showSmoke={false} /> */}
			<ClarityAnalytics />
			<Navbar />

			<main
				className={`flex flex-col ${
					location.pathname == "/"
						? "gap-6 md:gap-32 mt-20 md:mt-0 lg:mt-32"
						: "mt-20"
				} flex-1 flex flex-col`}
			>
				<Breadcrumbs
					showText={heroShowText}
					heading={heroHeading}
					textContent={heroTextContent}
					// onLoad={() => setIsHeroSectionLoaded(true)}
				/>

				{/* {isHeroSectionLoaded && ( */}
				<div ref={containerRef} className="px-4 lg:px-12">
					<Outlet />
				</div>
				{/* )} */}
			</main>

			<div className="p-0 md:p-16">
				<Footer />
			</div>

			{showBackToTop && (
				<button
					onClick={scrollToTop}
					className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-20 bg-steerBlack rounded-full p-2 flex items-center justify-center shadow-lg hover:scale-95 duration-300 ease-in transition-transform cursor-pointer"
					aria-label="Scroll to top"
				>
					<FaArrowUp className="text-white text-xl" />
				</button>
			)}

			{/* WhatsApp Button & Chatbox */}
			<div className="fixed bottom-4 right-4 z-30 flex flex-col items-end">
				{showChat && (
					<div className="bg-white shadow-lg rounded-xl w-80 mb-2 p-4 flex flex-col relative">
						<button
							className="absolute top-2 right-2 text-gray-400 hover:text-red-500 cursor-pointer"
							onClick={() => setShowChat(false)}
							aria-label="Close chat"
						>
							<FaTimes size={20} />
						</button>

						<div className="flex items-center mb-4">
							<img
								src={PROFILE_PIC}
								alt="Profile"
								className="w-10 h-10 rounded-full border-2 border-steerOrange mr-2"
							/>

							<div className="font-bold">{PROFILE_NAME}</div>
						</div>

						<div className="bg-gray-100 rounded-lg p-3 mb-2 self-start max-w-[80%]">
							<span className="text-sm">Hi, how can I help you?</span>
						</div>

						<div className="flex mt-2">
							<input
								type="text"
								className="flex-1 border rounded-l-lg px-2 py-1 text-sm"
								placeholder="Type your message..."
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleSend();
								}}
							/>

							<button
								className="bg-green-500 text-white px-4 py-1 rounded-r-lg cursor-pointer"
								onClick={handleSend}
							>
								Send
							</button>
						</div>
					</div>
				)}
				<button
					onClick={() => setShowChat((s) => !s)}
					className="bg-green-500 text-white rounded-full p-2 shadow-lg flex items-center cursor-pointer"
					aria-label="Open WhatsApp chat"
				>
					<FaWhatsapp className="text-xl" />
				</button>
			</div>
		</div>
	);
}

MainLayout.propTypes = {
	heroShowText: PropTypes.bool,
	heroHeading: PropTypes.string,
	heroTextContent: PropTypes.string,
};

export default MainLayout;
